import * as React from 'react';
import Select, { components, OptionProps, StylesConfig } from 'react-select';
import { SocialSecurityFund, SocialSecuritySubFund } from '../api/client';

// Define option type
interface OptionType {
  value: number;
  label: string;
  description?: string;
}

// Custom option component that displays the description under the name
const CustomOption: React.FC<OptionProps<OptionType, false>> = (props) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div>
        <div>{data.label}</div>
        {data.description && (
          <div className="text-sm text-gray-500">{data.description}</div>
        )}
      </div>
    </components.Option>
  );
};

// Custom styles for the select component
const customStyles: StylesConfig<OptionType, false> = {
  control: (provided) => ({
    ...provided,
    borderWidth: '2px',
    borderColor: 'black',
    borderRadius: '0',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'black',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#f0f0f0' : 'white',
    color: 'black',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  }),
};

interface SocialSecurityFundSelectProps {
  funds: SocialSecurityFund[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder: string;
  className?: string;
  isDisabled?: boolean;
}

export const SocialSecurityFundSelect: React.FC<SocialSecurityFundSelectProps> = ({
  funds,
  value,
  onChange,
  placeholder,
  className,
  isDisabled = false,
}) => {
  const options: OptionType[] = React.useMemo(() => 
    funds.map((fund) => ({
      value: fund.id,
      label: fund.name,
      description: fund.description,
    })), [funds]);

  // Find the selected option based on the value prop
  const selectedOption = React.useMemo(() => {
    if (value === null || value === undefined) {
      return null;
    }
    
    const option = options.find((option) => option.value === value);
    return option || null;
  }, [options, value]);

  return (
    <Select<OptionType, false>
      options={options}
      value={selectedOption}
      onChange={(option) => onChange(option ? option.value : null)}
      placeholder={placeholder}
      className={className}
      styles={customStyles}
      components={{ Option: CustomOption }}
      isClearable
      isDisabled={isDisabled}
    />
  );
};

interface SocialSecuritySubFundSelectProps {
  subFunds: SocialSecuritySubFund[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder: string;
  className?: string;
  isDisabled?: boolean;
}

export const SocialSecuritySubFundSelect: React.FC<SocialSecuritySubFundSelectProps> = ({
  subFunds,
  value,
  onChange,
  placeholder,
  className,
  isDisabled = false,
}) => {
  const options: OptionType[] = React.useMemo(() => 
    subFunds.map((subFund) => ({
      value: subFund.id,
      label: subFund.name,
      description: subFund.description,
    })), [subFunds]);

  // Find the selected option based on the value prop
  const selectedOption = React.useMemo(() => {
    if (value === null || value === undefined) {
      return null;
    }
    
    const option = options.find((option) => option.value === value);
    return option || null;
  }, [options, value]);

  return (
    <Select<OptionType, false>
      options={options}
      value={selectedOption}
      onChange={(option) => onChange(option ? option.value : null)}
      placeholder={placeholder}
      className={className}
      styles={customStyles}
      components={{ Option: CustomOption }}
      isDisabled={isDisabled}
      isClearable
    />
  );
}; 